import { createContext, memo, useContext } from 'react';
import { Handle, NodeProps, Position, Node } from '@xyflow/react';
import { Typography, makeStyles, useTheme } from '@material-ui/core';
import { EdgeCategory, NodeCategory } from './graph';
import {
  EntityHealth,
  STATE_COLORS,
  STATE_LABELS,
  SEVERITY_MARKS,
  healthState,
  issueText,
  problemCount,
} from '../health/api';
import { GROUP_HEADER, NODE_HEIGHT, NODE_WIDTH } from './layout';

// Цвета типов приглушены и близки по яркости: насыщенные красный, жёлтый и зелёный зарезервированы
// за состоянием сервисов, чтобы тип узла не спорил с индикацией здоровья.
export const CATEGORY_COLORS: Record<NodeCategory, string> = {
  component: '#5C71B7',
  api: '#4F9A8E',
  resource: '#9A84C4',
  external: '#8A8FA8',
};

export const CATEGORY_LABELS: Record<NodeCategory, string> = {
  component: 'Компоненты',
  api: 'API',
  resource: 'Ресурсы',
  external: 'Внешние сервисы',
};

export const EDGE_COLORS: Record<EdgeCategory, string> = {
  dependsOn: '#8A8FA8',
  providesApi: '#4F9A8E',
  consumesApi: '#5C71B7',
};

export const EDGE_LABELS: Record<EdgeCategory, string> = {
  dependsOn: 'Зависит от',
  providesApi: 'Предоставляет API',
  consumesApi: 'Использует API',
};

export type EntityNodeData = {
  label: string;
  subtitle: string;
  category: NodeCategory;
};

export type GroupNodeData = {
  label: string;
};

// Подсветка передаётся контекстом, а не через data узлов: новый массив узлов на каждое наведение
// заставлял ReactFlow заново измерять их и на кадр скрывать, курсор проваливался на фон, наведение
// сбрасывалось, и так по кругу (мигание и скачущий курсор).
export type Highlight = {
  nodes?: Set<string>; // без подсветки — undefined
  groups?: Set<string>;
  selectedId?: string;
};
export const HighlightContext = createContext<Highlight>({});

// Состояние сущностей в Kubernetes (плагин kubernetes-diagnostics) по entity ref. Тоже контекстом:
// обновление раз в минуту не должно пересоздавать узлы.
export type GroupHealth = { critical: number; warning: number };
export const HealthContext = createContext<{
  byEntity: Map<string, EntityHealth>;
  byGroup: Map<string, GroupHealth>;
}>({ byEntity: new Map(), byGroup: new Map() });

export type EntityFlowNode = Node<EntityNodeData, 'entity'>;
export type GroupFlowNode = Node<GroupNodeData, 'system'>;

const useStyles = makeStyles(theme => ({
  entity: {
    position: 'relative',
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
    boxSizing: 'border-box',
    padding: '6px 10px 6px 12px',
    borderRadius: 8,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 5,
    boxShadow: theme.shadows[1],
    cursor: 'pointer',
    transition: 'opacity 120ms, box-shadow 120ms',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  label: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    fontSize: 11,
    lineHeight: '14px',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  healthDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  // Значок проблем: форма (▲ внимание, ✕ авария) и число, читается и без цвета.
  healthBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    padding: '0 5px',
    borderRadius: 8,
    fontSize: 10,
    lineHeight: '15px',
    fontWeight: 700,
    color: '#fff',
  },
  // Авария: кольцо расходится от узла, чтобы его было видно и на мелком масштабе.
  pulse: {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -3,
      borderRadius: 10,
      border: `2px solid ${STATE_COLORS.critical}`,
      animation: '$pulse 1.6s ease-out infinite',
      pointerEvents: 'none',
    },
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.9, transform: 'scale(1)' },
    '100%': { opacity: 0, transform: 'scale(1.06, 1.25)' },
  },
  groupBadges: { display: 'flex', gap: 6, alignItems: 'center' },
  groupBadge: {
    padding: '0 6px',
    borderRadius: 8,
    fontSize: 11,
    lineHeight: '17px',
    fontWeight: 700,
    color: '#fff',
  },
  handle: {
    opacity: 0,
    width: 1,
    height: 1,
    minWidth: 1,
    minHeight: 1,
    border: 'none',
  },
  group: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: 12,
    border: `1.5px dashed ${theme.palette.divider}`,
    background:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(6, 10, 45, 0.03)',
    transition: 'opacity 120ms',
  },
  groupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: GROUP_HEADER,
    lineHeight: `${GROUP_HEADER}px`,
    padding: '0 14px',
    fontWeight: 700,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
}));

// Точки крепления связей со всех сторон: сторону выбирает страница по взаимному расположению узлов
// (к полосе общих ресурсов — сверху или снизу, внутри системы — слева направо).
const SIDES: [string, Position][] = [
  ['l', Position.Left],
  ['r', Position.Right],
  ['t', Position.Top],
  ['b', Position.Bottom],
];

export const EntityNode = memo(({ id, data }: NodeProps<EntityFlowNode>) => {
  const classes = useStyles();
  const theme = useTheme();
  const highlight = useContext(HighlightContext);
  const color = CATEGORY_COLORS[data.category];
  const dimmed = !!highlight.nodes && !highlight.nodes.has(id);
  const health = useContext(HealthContext).byEntity.get(id);
  const state = health ? healthState(health) : undefined;
  const problem = state === 'critical' || state === 'warning';
  const stateColor = state ? STATE_COLORS[state] : undefined;
  const rings = [
    problem && `0 0 0 2px ${stateColor}`,
    id === highlight.selectedId && `0 0 0 ${problem ? 4 : 2}px ${color}`,
  ].filter(Boolean);
  const healthTitle = health && state
    ? `\n${STATE_LABELS[state]}${health.issues
        .filter(i => i.severity !== 'info')
        .map(i => `\n${SEVERITY_MARKS[i.severity]} ${issueText(i)}`)
        .join('')}`
    : '';
  return (
    <div
      className={`${classes.entity} ${state === 'critical' ? classes.pulse : ''}`}
      style={{
        borderLeftColor: color,
        opacity: dimmed ? 0.2 : 1,
        boxShadow: rings.length ? rings.join(', ') : undefined,
        backgroundImage:
          state === 'critical'
            ? 'linear-gradient(rgba(229, 56, 59, 0.14), rgba(229, 56, 59, 0.14))'
            : undefined,
        outlineColor: theme.palette.primary.main,
      }}
      title={`${data.label}${healthTitle}`}
    >
      {health && problem && (
        <span className={classes.healthBadge} style={{ background: stateColor }}>
          {state === 'critical' ? '✕' : '▲'} {problemCount(health)}
        </span>
      )}
      {health && !problem && (
        <span className={classes.healthDot} style={{ background: stateColor }} />
      )}
      {SIDES.map(([side, position]) => (
        <Handle key={`t-${side}`} id={`t-${side}`} type="target" position={position} className={classes.handle} />
      ))}
      <Typography className={classes.label}>{data.label}</Typography>
      <Typography className={classes.subtitle}>{data.subtitle}</Typography>
      {SIDES.map(([side, position]) => (
        <Handle key={`s-${side}`} id={`s-${side}`} type="source" position={position} className={classes.handle} />
      ))}
    </div>
  );
});

export const GroupNode = memo(({ id, data }: NodeProps<GroupFlowNode>) => {
  const classes = useStyles();
  const { groups } = useContext(HighlightContext);
  const health = useContext(HealthContext).byGroup.get(id);
  return (
    <div className={classes.group} style={{ opacity: groups && !groups.has(id) ? 0.35 : 1 }}>
      <div className={classes.groupTitle}>
        {data.label}
        {health && (health.critical > 0 || health.warning > 0) && (
          <span className={classes.groupBadges}>
            {health.critical > 0 && (
              <span className={classes.groupBadge} style={{ background: STATE_COLORS.critical }}>
                ✕ {health.critical}
              </span>
            )}
            {health.warning > 0 && (
              <span className={classes.groupBadge} style={{ background: STATE_COLORS.warning }}>
                ▲ {health.warning}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
});

export const nodeTypes = { entity: EntityNode, system: GroupNode };
